<template>
  <AdminLayout>
    <div class="p-6">
      <!-- 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-slate-800">FAQ 관리</h1>
          <p class="text-sm text-slate-500 mt-1">자주 묻는 질문을 관리합니다</p>
        </div>
        <button
          class="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition shadow-lg shadow-blue-600/20"
          @click="openCreateModal"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          FAQ 추가
        </button>
      </div>

      <!-- 통계 카드 -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">전체 FAQ</p>
          <p class="text-2xl font-bold text-slate-800">{{ faqs.length }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">활성화</p>
          <p class="text-2xl font-bold text-green-600">{{ activeCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">비활성화</p>
          <p class="text-2xl font-bold text-slate-400">{{ faqs.length - activeCount }}</p>
        </div>
        <div class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-xs text-slate-500 mb-1">카테고리</p>
          <p class="text-2xl font-bold text-blue-600">{{ categories.length }}</p>
        </div>
      </div>

      <!-- 카테고리 필터 -->
      <div class="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition',
            selectedCategory === null
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="selectedCategory = null"
        >
          전체
        </button>
        <button
          v-for="cat in categories"
          :key="cat.id"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition',
            selectedCategory === cat.id
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
          @click="selectedCategory = cat.id"
        >
          {{ cat.icon }} {{ cat.label }}
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading && faqs.length === 0" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>

      <!-- FAQ 목록 -->
      <div v-else-if="filteredFaqs.length > 0" class="space-y-3">
        <div
          v-for="(faq, index) in filteredFaqs"
          :key="faq.id"
          class="bg-white rounded-2xl border border-slate-100 overflow-hidden"
        >
          <div class="p-4">
            <div class="flex items-start gap-3">
              <!-- 순서 -->
              <div class="flex flex-col gap-1">
                <button
                  class="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30"
                  :disabled="index === 0"
                  @click="moveUp(faq, index)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                  </svg>
                </button>
                <button
                  class="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30"
                  :disabled="index === filteredFaqs.length - 1"
                  @click="moveDown(faq, index)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
              </div>

              <!-- 내용 -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-2">
                  <span class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                    {{ getCategoryLabel(faq.category) }}
                  </span>
                  <span
                    :class="[
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      faq.isActive
                        ? 'bg-green-100 text-green-600'
                        : 'bg-slate-100 text-slate-400'
                    ]"
                  >
                    {{ faq.isActive ? '활성' : '비활성' }}
                  </span>
                </div>
                <h3 class="font-bold text-slate-800 mb-1">Q. {{ faq.question }}</h3>
                <p class="text-sm text-slate-600 line-clamp-2">A. {{ getPlainText(faq.answer) }}</p>
              </div>

              <!-- 액션 -->
              <div class="flex items-center gap-1">
                <button
                  class="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                  @click="openEditModal(faq)"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
                <button
                  :class="[
                    'p-2 rounded-lg transition',
                    faq.isActive
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-slate-400 hover:bg-slate-50'
                  ]"
                  @click="handleToggleActive(faq)"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path v-if="faq.isActive" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path v-if="faq.isActive" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                  </svg>
                </button>
                <button
                  class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                  @click="confirmDelete(faq)"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 빈 상태 -->
      <div v-else class="text-center py-12 bg-white rounded-2xl border border-slate-100">
        <span class="text-4xl mb-4 block">❓</span>
        <h3 class="text-lg font-bold text-slate-800 mb-2">등록된 FAQ가 없습니다</h3>
        <p class="text-sm text-slate-500">FAQ를 추가해주세요</p>
      </div>

      <!-- 생성/수정 모달 -->
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        @click.self="closeModal"
      >
        <div class="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div class="p-6 border-b border-slate-100">
            <h2 class="text-xl font-bold text-slate-800">
              {{ isEditing ? 'FAQ 수정' : 'FAQ 추가' }}
            </h2>
          </div>

          <form class="p-6 space-y-4" @submit.prevent="handleSubmit">
            <!-- 카테고리 -->
            <div class="form-group">
              <label class="form-label">카테고리 <span class="required">*</span></label>
              <select v-model="form.category" class="form-select">
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.icon }} {{ cat.label }}
                </option>
              </select>
            </div>

            <!-- 질문 -->
            <div class="form-group">
              <label class="form-label">질문 <span class="required">*</span></label>
              <input
                v-model="form.question"
                type="text"
                class="form-input"
                placeholder="자주 묻는 질문을 입력하세요"
              />
            </div>

            <!-- 답변 (WYSIWYG) -->
            <div class="form-group">
              <label class="form-label">답변 <span class="required">*</span></label>
              <div class="wysiwyg-editor">
                <!-- 에디터 툴바 -->
                <div class="editor-toolbar">
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('bold') }"
                      @click="execCommand('bold')"
                      title="굵게"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('italic') }"
                      @click="execCommand('italic')"
                      title="기울임"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('underline') }"
                      @click="execCommand('underline')"
                      title="밑줄"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('insertUnorderedList')"
                      title="글머리 기호"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="execCommand('insertOrderedList')"
                      title="번호 매기기"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="insertLink"
                      title="링크 삽입"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- 에디터 영역 -->
                <div
                  ref="editorRef"
                  class="editor-content"
                  contenteditable="true"
                  @input="onEditorInput"
                  @mouseup="updateToolbarState"
                  @keyup="updateToolbarState"
                ></div>
              </div>
            </div>

            <!-- 버튼 -->
            <div class="flex gap-3 pt-4">
              <button
                type="button"
                class="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition"
                @click="closeModal"
              >
                취소
              </button>
              <button
                type="submit"
                class="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50"
                :disabled="!isFormValid || submitting"
              >
                {{ submitting ? '처리 중...' : (isEditing ? '수정' : '추가') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- 삭제 확인 모달 -->
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        @click.self="showDeleteConfirm = false"
      >
        <div class="bg-white rounded-3xl w-full max-w-sm p-6 text-center">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-slate-800 mb-2">FAQ를 삭제할까요?</h3>
          <p class="text-sm text-slate-500 mb-6">삭제된 FAQ는 복구할 수 없습니다.</p>
          <div class="flex gap-3">
            <button
              class="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition"
              @click="showDeleteConfirm = false"
            >
              취소
            </button>
            <button
              class="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition"
              @click="handleDelete"
            >
              삭제
            </button>
          </div>
        </div>
      </div>

      <!-- 토스트 -->
      <transition name="toast">
        <div
          v-if="showToast"
          :class="[
            'fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full shadow-lg z-50',
            toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          ]"
        >
          {{ toastMessage }}
        </div>
      </transition>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useFaq, FAQ_CATEGORIES } from '@/composables/common/useFaq'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'FaqManageView',
  components: {
    AdminLayout
  },
  setup() {
    const { loading, getAllFaqs, createFaq, updateFaq, toggleFaqActive, updateFaqOrder, deleteFaq } = useFaq()

    const faqs = ref([])
    const categories = FAQ_CATEGORIES
    const selectedCategory = ref(null)
    const showModal = ref(false)
    const isEditing = ref(false)
    const editingId = ref(null)
    const submitting = ref(false)

    // WYSIWYG 에디터
    const editorRef = ref(null)
    const activeFormats = ref({
      bold: false,
      italic: false,
      underline: false
    })

    const form = ref({
      category: 'service',
      question: '',
      answer: ''
    })

    // 삭제 확인
    const showDeleteConfirm = ref(false)
    const deleteTarget = ref(null)

    // 토스트
    const showToast = ref(false)
    const toastMessage = ref('')
    const toastType = ref('success')

    // 필터링된 FAQ
    const filteredFaqs = computed(() => {
      if (!selectedCategory.value) return faqs.value
      return faqs.value.filter(f => f.category === selectedCategory.value)
    })

    // 활성 FAQ 수
    const activeCount = computed(() => {
      return faqs.value.filter(f => f.isActive).length
    })

    // 폼 유효성
    const isFormValid = computed(() => {
      return form.value.question.trim() && form.value.answer.trim()
    })

    // 카테고리 라벨 가져오기
    const getCategoryLabel = (categoryId) => {
      const cat = categories.find(c => c.id === categoryId)
      return cat ? `${cat.icon} ${cat.label}` : categoryId
    }

    // HTML에서 텍스트 추출
    const getPlainText = (html) => {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '')
    }

    // WYSIWYG 에디터: execCommand 실행
    const execCommand = (command, value = null) => {
      editorRef.value?.focus()
      document.execCommand(command, false, value)
      updateToolbarState()
      syncEditorContent()
    }

    // 포맷 활성 상태 확인
    const isFormatActive = (format) => {
      return activeFormats.value[format]
    }

    // 툴바 상태 업데이트
    const updateToolbarState = () => {
      activeFormats.value.bold = document.queryCommandState('bold')
      activeFormats.value.italic = document.queryCommandState('italic')
      activeFormats.value.underline = document.queryCommandState('underline')
    }

    // 에디터 내용 동기화
    const syncEditorContent = () => {
      if (editorRef.value) {
        form.value.answer = editorRef.value.innerHTML
      }
    }

    // 에디터 입력 이벤트
    const onEditorInput = () => {
      syncEditorContent()
    }

    // 링크 삽입
    const insertLink = () => {
      const selection = window.getSelection()
      const selectedText = selection.toString() || '링크'
      const url = prompt('링크 URL을 입력하세요:', 'https://')
      if (!url) return

      if (selection.toString()) {
        execCommand('createLink', url)
      } else {
        const linkHtml = `<a href="${url}" target="_blank">${selectedText}</a>`
        execCommand('insertHTML', linkHtml)
      }
    }

    // 에디터 내용 설정
    const setEditorContent = (content) => {
      nextTick(() => {
        if (editorRef.value) {
          editorRef.value.innerHTML = content || ''
        }
      })
    }

    // 토스트 표시
    const showToastMessage = (message, type = 'success') => {
      toastMessage.value = message
      toastType.value = type
      showToast.value = true
      setTimeout(() => {
        showToast.value = false
      }, 3000)
    }

    // FAQ 목록 로드
    const loadFaqs = async () => {
      faqs.value = await getAllFaqs()
    }

    // 모달 열기 (생성)
    const openCreateModal = () => {
      isEditing.value = false
      editingId.value = null
      form.value = {
        category: 'service',
        question: '',
        answer: ''
      }
      showModal.value = true
      setEditorContent('')
    }

    // 모달 열기 (수정)
    const openEditModal = (faq) => {
      isEditing.value = true
      editingId.value = faq.id
      form.value = {
        category: faq.category,
        question: faq.question,
        answer: faq.answer
      }
      showModal.value = true
      setEditorContent(faq.answer)
    }

    // 모달 닫기
    const closeModal = () => {
      showModal.value = false
      form.value = {
        category: 'service',
        question: '',
        answer: ''
      }
    }

    // 제출 처리
    const handleSubmit = async () => {
      if (!isFormValid.value) return

      submitting.value = true
      try {
        if (isEditing.value) {
          await updateFaq(editingId.value, form.value)
          showToastMessage('FAQ가 수정되었습니다')
        } else {
          await createFaq(form.value)
          showToastMessage('FAQ가 추가되었습니다')
        }
        closeModal()
        await loadFaqs()
      } catch (err) {
        showToastMessage('처리 중 오류가 발생했습니다', 'error')
      } finally {
        submitting.value = false
      }
    }

    // 활성화 토글
    const handleToggleActive = async (faq) => {
      try {
        await toggleFaqActive(faq.id, !faq.isActive)
        showToastMessage(faq.isActive ? 'FAQ가 비활성화되었습니다' : 'FAQ가 활성화되었습니다')
        await loadFaqs()
      } catch (err) {
        showToastMessage('상태 변경 중 오류가 발생했습니다', 'error')
      }
    }

    // 순서 변경 - 위로
    const moveUp = async (faq, index) => {
      if (index === 0) return
      const prevFaq = filteredFaqs.value[index - 1]
      try {
        await updateFaqOrder(faq.id, prevFaq.order)
        await updateFaqOrder(prevFaq.id, faq.order)
        await loadFaqs()
      } catch (err) {
        showToastMessage('순서 변경 중 오류가 발생했습니다', 'error')
      }
    }

    // 순서 변경 - 아래로
    const moveDown = async (faq, index) => {
      if (index === filteredFaqs.value.length - 1) return
      const nextFaq = filteredFaqs.value[index + 1]
      try {
        await updateFaqOrder(faq.id, nextFaq.order)
        await updateFaqOrder(nextFaq.id, faq.order)
        await loadFaqs()
      } catch (err) {
        showToastMessage('순서 변경 중 오류가 발생했습니다', 'error')
      }
    }

    // 삭제 확인
    const confirmDelete = (faq) => {
      deleteTarget.value = faq
      showDeleteConfirm.value = true
    }

    // 삭제 처리
    const handleDelete = async () => {
      if (!deleteTarget.value) return

      try {
        await deleteFaq(deleteTarget.value.id)
        showToastMessage('FAQ가 삭제되었습니다')
        showDeleteConfirm.value = false
        deleteTarget.value = null
        await loadFaqs()
      } catch (err) {
        showToastMessage('삭제 중 오류가 발생했습니다', 'error')
      }
    }

    onMounted(() => {
      loadFaqs()
    })

    return {
      loading,
      faqs,
      categories,
      selectedCategory,
      filteredFaqs,
      activeCount,
      showModal,
      isEditing,
      form,
      submitting,
      isFormValid,
      showDeleteConfirm,
      deleteTarget,
      showToast,
      toastMessage,
      toastType,
      editorRef,
      getCategoryLabel,
      getPlainText,
      openCreateModal,
      openEditModal,
      closeModal,
      handleSubmit,
      handleToggleActive,
      moveUp,
      moveDown,
      confirmDelete,
      handleDelete,
      // WYSIWYG
      execCommand,
      isFormatActive,
      updateToolbarState,
      onEditorInput,
      insertLink
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-semibold text-slate-700;
}

.required {
  @apply text-red-500;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20;
}

.form-select {
  @apply w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20;
}

/* WYSIWYG 에디터 */
.wysiwyg-editor {
  @apply border border-slate-200 rounded-xl overflow-hidden bg-white;
}

.editor-toolbar {
  @apply flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200;
}

.toolbar-group {
  @apply flex items-center gap-0.5;
}

.toolbar-btn {
  @apply w-8 h-8 flex items-center justify-center text-slate-600 hover:bg-white hover:text-blue-600 rounded-lg transition;
}

.toolbar-btn.active {
  @apply bg-blue-100 text-blue-600;
}

.toolbar-divider {
  @apply w-px h-6 bg-slate-200 mx-1;
}

.editor-content {
  @apply min-h-[150px] max-h-[250px] overflow-y-auto p-4 text-sm text-slate-700 leading-relaxed outline-none;
}

.editor-content:empty::before {
  content: '답변 내용을 입력하세요...';
  @apply text-slate-400;
}

.editor-content:focus {
  @apply bg-blue-50/30;
}

/* 토스트 애니메이션 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
</style>
